import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../config/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  /**
   * PIN login: pass optional username for legacy support.
   * Returns needsStoreSelection + pickToken + stores when user has multiple outlets.
   */
  const loginWithPin = async (pin, usernameOptional) => {
    try {
      const body =
        usernameOptional != null && String(usernameOptional).trim()
          ? { pin, username: String(usernameOptional).trim() }
          : { pin };
      const response = await api.post('/auth/login-pin', body);
      const data = response.data;

      if (data.needsStoreSelection && data.pickToken) {
        return {
          needsStoreSelection: true,
          pickToken: data.pickToken,
          stores: data.stores || []
        };
      }

      const newToken = data.token;
      const userData = data.user;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser({
        ...userData,
        active_store_id: data.active_store_id != null ? data.active_store_id : null
      });

      return {
        success: true,
        user: {
          ...userData,
          active_store_id: data.active_store_id != null ? data.active_store_id : null
        },
        active_store_id: data.active_store_id ?? null
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'PIN login failed'
      };
    }
  };

  const completePinStoreLogin = async (pickToken, storeId) => {
    try {
      const response = await api.post('/auth/login-pin/store', {
        pickToken,
        storeId: parseInt(storeId, 10)
      });
      const { token: newToken, user: userData, active_store_id: activeStoreId } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser({
        ...userData,
        active_store_id: activeStoreId != null ? activeStoreId : null
      });
      return { success: true, user: userData, active_store_id: activeStoreId };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Could not select outlet'
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, loginWithPin, completePinStoreLogin, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};
